import { observer } from 'mobx-react-lite'
import {
  MembershipRow,
  MembershipsTab,
} from '@/features/user-management/MembershipsTab'
import {
  type MembershipAddAction,
  type MembershipsTabViewModel,
} from './MembershipsTabViewModel'
import type { MembershipItemViewModel } from './MembershipItemViewModel'
import { useFeatures } from '@/contexts/root-store-context'

const addButtonTestIds: Record<MembershipAddAction, string> = {
  JoinWorkspaceAsAdmin: 'memberships-join-workspace-as-admin',
  AddCustomerToWorkspace: 'memberships-add-customer-to-workspace',
  AddUserToWorkspace: 'memberships-add-user-to-workspace',
}

export interface MembershipsTabContainerProps {
  vm: MembershipsTabViewModel
}

export const MembershipsTabContainer = observer(
  function MembershipsTabContainer({ vm }: MembershipsTabContainerProps) {
    const { shellInfo } = useFeatures(({ shell }) => ({
      shellInfo: shell.shellInfo,
    }))

    return (
      <MembershipsTab
        membershipsCount={vm.membershipsCount}
        searchText={vm.searchText.value}
        onSearchTextChange={vm.searchText.change}
        addButtonTestId={addButtonTestIds[vm.addAction]}
        onAdd={vm.add}
        currentTab={vm.tabController.current.tab}
        onTabChange={vm.setCurrentMembershipTab}
        isEmpty={!vm.currentMembershipVms.length}
        isSearchFilterApplied={!vm.isSearchTextEmpty}
        corporateMode={shellInfo.corporateMode}
        onClearSearchFilter={vm.clearFilters}
      >
        {vm.currentMembershipVms.map((membershipVm) => (
          <MembershipRowContainer
            vm={membershipVm}
            key={membershipVm.membership.workspace.id}
          />
        ))}
      </MembershipsTab>
    )
  },
)

interface MembershipRowContainerProps {
  vm: MembershipItemViewModel
}

const MembershipRowContainer = observer(function MembershipRowContainer({
  vm,
}: MembershipRowContainerProps) {
  return (
    <MembershipRow
      workspaceId={vm.membership.workspace.id}
      workspaceName={vm.membership.workspace.name}
      workspaceIcon={vm.membership.workspace.branding.logos.icon}
      roleName={vm.roleName}
      dateCreated={vm.membership.member.dateCreated}
      isActive={vm.isSelected}
      isInactive={vm.membership.member.inactive}
      access={vm.access}
      canPromoteToOwner={vm.canPromoteToOwner}
      useMagicLink={vm.usesMagicLink}
      isGenerateMagicLinkPending={vm.generateMagicLink.pending}
      onView={vm.view}
      onRestore={vm.restore}
      onAddToTeam={vm.addToTeam}
      onEditRole={vm.editRole}
      onEditSkills={vm.editSkills}
      onManageCapacity={vm.manageCapacity}
      onRemoveFromWorkspace={vm.removeFromWorkspace}
      onPromoteToOwner={vm.promoteToOwner}
      onImpersonateAsClient={vm.impersonateAsClient}
      onImpersonateAsPro={vm.impersonateAsPro}
      onGenerateMagicLink={vm.generateMagicLink}
    />
  )
})
